/**
 * Chart overrides dispatcher.
 *
 * Routes to the correct per-chart-type override module based on
 * `chartType` and series types. Handles both:
 * - **Chart-level overrides** (pie, funnel, heatmap) that modify the whole option
 * - **Series-level overrides** (line, bar, scatter) that patch individual series
 */
import type { EChartsOption } from 'echarts';
import type { ChartStyleState, TypeStyle } from '../../../../../edit/visualConfig';
import { applyLineOverrides } from './line';
import { applyBarOverrides } from './bar';
import { applyScatterOverrides } from './scatter';
import { applyPieOverrides } from './pie';
import { applyFunnelOverrides } from './funnel';
import { applyHeatmapOverrides } from './heatmap';

export function applyChartOverrides(
  option: EChartsOption,
  vc: ChartStyleState,
  cs: TypeStyle,
  chartType?: string,
): EChartsOption {
  let result = { ...option };

  // ── Chart-level overrides (modify option beyond series) ──────────────
  if (chartType === 'pie') result = applyPieOverrides(result, cs);
  if (chartType === 'funnel') result = applyFunnelOverrides(result, cs);
  if (chartType === 'heatmap') result = applyHeatmapOverrides(result, cs, vc.palette);

  // ── Series-level overrides ───────────────────────────────────────────
  if (!Array.isArray(result.series)) return result;

  const allSeries = result.series as any[];

  // Pre-compute bar context (needed for stacked corner radius)
  const barIndices = allSeries
    .map((s, i) => (s.type === 'bar' ? i : -1))
    .filter((i) => i >= 0);
  const lastBarIdx = barIndices.length > 0 ? barIndices[barIndices.length - 1] : -1;
  const barCount = barIndices.length;

  result.series = allSeries.map((s: any, idx: number) => {
    if (s.type === 'line') return applyLineOverrides(s, cs);
    if (s.type === 'bar') return applyBarOverrides(s, cs, { seriesIdx: idx, lastBarIdx, barCount });
    if (s.type === 'scatter') return applyScatterOverrides(s, cs);
    return s;
  });

  return result;
}